import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  dbAccounts as db,
  auth,
} from "../../../../../../src/backend/firebaseConfig";
import {
  CHANGES_COLLECTION,
  PARAPLANNING_DOC_ID,
  CATALOGS_SUBCOLLECTION,
  CATEGORIES_SUBCOLLECTION,
  ITEMS_SUBCOLLECTION,
  VERSION_BATCHES_SUBCOLLECTION,
  PARAPLANNING_CATALOGS,
} from "../../constants/chargeConstants";
import { sortCatalogs, sortCategories, sortItems } from "../utils/groupCharges";
import { a } from "react-spring";

const rootDocRef = () => doc(db, CHANGES_COLLECTION, PARAPLANNING_DOC_ID);

const catalogDocRef = (catalogId) =>
  doc(
    db,
    CHANGES_COLLECTION,
    PARAPLANNING_DOC_ID,
    CATALOGS_SUBCOLLECTION,
    catalogId,
  );

const categoryDocRef = (catalogId, categoryId) =>
  doc(
    db,
    CHANGES_COLLECTION,
    PARAPLANNING_DOC_ID,
    CATALOGS_SUBCOLLECTION,
    catalogId,
    CATEGORIES_SUBCOLLECTION,
    categoryId,
  );

const itemsCollectionRef = (catalogId, categoryId) =>
  collection(
    db,
    CHANGES_COLLECTION,
    PARAPLANNING_DOC_ID,
    CATALOGS_SUBCOLLECTION,
    catalogId,
    CATEGORIES_SUBCOLLECTION,
    categoryId,
    ITEMS_SUBCOLLECTION,
  );

const itemDocRef = (catalogId, categoryId, itemId) =>
  doc(
    db,
    CHANGES_COLLECTION,
    PARAPLANNING_DOC_ID,
    CATALOGS_SUBCOLLECTION,
    catalogId,
    CATEGORIES_SUBCOLLECTION,
    categoryId,
    ITEMS_SUBCOLLECTION,
    itemId,
  );

const versionBatchesCollectionRef = () =>
  collection(
    db,
    CHANGES_COLLECTION,
    PARAPLANNING_DOC_ID,
    VERSION_BATCHES_SUBCOLLECTION,
  );

const buildUserMeta = (user) => ({
  uid: user?.uid || "",
  name: user?.displayName || "",
  email: auth.currentUser?.email || "",
});

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

export const seedParaplanningStructure = async (user) => {
  await setDoc(
    rootDocRef(),
    {
      id: PARAPLANNING_DOC_ID,
      name: "Paraplanning",
      //  currentVersionId: "",
      updatedAt: serverTimestamp(),
      updatedBy: buildUserMeta(user),
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );

  for (const catalog of PARAPLANNING_CATALOGS) {
    await setDoc(
      catalogDocRef(catalog.id),
      {
        id: catalog.id,
        name: catalog.name,
        code: catalog.code,
        displayOrder: catalog.displayOrder,
        isActive: true,
        currency: "GBP",
        updatedAt: serverTimestamp(),
        updatedBy: buildUserMeta(user),
        createdAt: serverTimestamp(),
      },
      { merge: true },
    );

    for (const category of catalog.categories || []) {
      await setDoc(
        categoryDocRef(catalog.id, category.id),
        {
          id: category.id,
          name: category.name,
          displayOrder: category.displayOrder,
          isActive: true,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        },
        { merge: true },
      );
    }
  }
};

const fetchCatalog = async (catalogConfig) => {
  const catalogSnap = await getDoc(catalogDocRef(catalogConfig.id));

  const categoriesSnap = await getDocs(
    collection(
      db,
      CHANGES_COLLECTION,
      PARAPLANNING_DOC_ID,
      CATALOGS_SUBCOLLECTION,
      catalogConfig.id,
      CATEGORIES_SUBCOLLECTION,
    ),
  );

  const dbCategories = categoriesSnap.docs.map((snap) => ({
    id: snap.id,
    ...snap.data(),
  }));

  const categoriesToUse =
    dbCategories.length > 0
      ? dbCategories
      : (catalogConfig.categories || []).map((category) => ({
          ...category,
          isActive: true,
        }));

  const populatedCategories = [];

  for (const category of categoriesToUse) {
    const itemsSnap = await getDocs(
      itemsCollectionRef(catalogConfig.id, category.id),
    );

    populatedCategories.push({
      ...category,
      items: sortItems(
        itemsSnap.docs.map((itemSnap) => ({
          id: itemSnap.id,
          ...itemSnap.data(),
        })),
      ),
    });
  }

  return {
    id: catalogConfig.id,
    name: catalogSnap.exists() ? catalogSnap.data().name : catalogConfig.name,
    code: catalogSnap.exists() ? catalogSnap.data().code : catalogConfig.code,
    currency: catalogSnap.exists()
      ? catalogSnap.data().currency || "GBP"
      : "GBP",
    displayOrder: catalogSnap.exists()
      ? catalogSnap.data().displayOrder || catalogConfig.displayOrder
      : catalogConfig.displayOrder,
    currentVersionId: catalogSnap.exists()
      ? catalogSnap.data().currentVersionId || ""
      : "",
    categories: sortCategories(populatedCategories),
  };
};

export const fetchAllParaplanningCatalogs = async () => {
  const catalogs = [];

  for (const catalog of PARAPLANNING_CATALOGS) {
    const result = await fetchCatalog(catalog);
    catalogs.push(result);
  }

  return sortCatalogs(catalogs);
};

export const fetchAllVersionBatches = async () => {
  try {
    const q = query(
      versionBatchesCollectionRef(),
      orderBy("publishedAt", "desc"),
    );
    const snap = await getDocs(q);

    return snap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (error) {
    const snap = await getDocs(versionBatchesCollectionRef());

    return snap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  }
};

export const saveChargeItem = async ({ catalogId, categoryId, item, user }) => {
  await setDoc(
    categoryDocRef(catalogId, categoryId),
    {
      id: categoryId,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  const payload = {
    id: item.id || slugify(item.name) || `item_${Date.now()}`,
    name: item.name?.trim() || "",
    description: item.description?.trim() || "",
    amount: Number(item.amount || 0),
    amountPrefix: item.amountPrefix || "",
    amountType: item.amountType || "fixed",
    currency: item.currency || "GBP",
    displayOrder: Number(item.displayOrder || 999),
    isActive: item.isActive !== false,
    visibleOnFrontend: item.visibleOnFrontend !== false,
    modifiers: (item.modifiers || []).map((m) => ({
      id: m.id,
      label: m.label,
      amount: Number(m.amount || 0),
      amountType: m.amountType || "fixed",
    })),
    updatedAt: serverTimestamp(),
    updatedBy: buildUserMeta(user),
  };

  const itemId = payload.id;

  await setDoc(
    itemDocRef(catalogId, categoryId, itemId),
    {
      ...payload,
      createdAt: item.createdAt || serverTimestamp(),
    },
    { merge: true },
  );

  return {
    ...item,
    ...payload,
    id: itemId,
  };
};

export const deleteChargeItem = async ({ catalogId, categoryId, itemId }) => {
  await deleteDoc(itemDocRef(catalogId, categoryId, itemId));
};

export const publishAllCatalogVersions = async ({
  catalogs,
  notesByCatalog,
  user,
}) => {
  const versionId = `v${new Date()
    .toISOString()
    .replace(/[-:TZ.]/g, "")
    .slice(0, 14)}`;

  const catalogEntries = Object.values(catalogs || {});

  const payload = {
    versionId,
    label: new Date().toLocaleString("en-GB"),
    publishedAt: serverTimestamp(),
    publishedBy: buildUserMeta(user),
    catalogs: {},
  };

  for (const catalog of catalogEntries) {
    payload.catalogs[catalog.id] = {
      catalogId: catalog.id,
      catalogName: catalog.name,
      notes: notesByCatalog?.[catalog.id] || "",
      snapshot: {
        categories: (catalog.categories || []).map((category) => ({
          id: category.id,
          name: category.name,
          displayOrder: category.displayOrder || 999,
          items: (category.items || []).map((item) => ({
            id: item.id,
            name: item.name,
            description: item.description || "",
            amount: Number(item.amount || 0),
            amountPrefix: item.amountPrefix || "",
            amountType: item.amountType || "fixed",
            currency: item.currency || "GBP",
            displayOrder: Number(item.displayOrder || 999),
            isActive: item.isActive !== false,
            visibleOnFrontend: item.visibleOnFrontend !== false,
            modifiers: (item.modifiers || []).map((modifier) => ({
              id: modifier.id,
              label: modifier.label || "",
              amount: Number(modifier.amount || 0),
              amountType: modifier.amountType || "fixed",
            })),
          })),
        })),
      },
    };

    await setDoc(
      catalogDocRef(catalog.id),
      {
        currentVersionId: versionId,
        updatedAt: serverTimestamp(),
        updatedBy: buildUserMeta(user),
      },
      { merge: true },
    );
  }

  //  SAVE SHARED CURRENT VERSION ID ON BASE DOC
  await setDoc(
    rootDocRef(),
    {
      currentVersionId: versionId,
      updatedAt: serverTimestamp(),
      updatedBy: buildUserMeta(user),
    },
    { merge: true },
  );

  await setDoc(doc(versionBatchesCollectionRef(), versionId), payload);

  return versionId;
};

export const fetchParaplanningBaseDoc = async () => {
  const snap = await getDoc(rootDocRef());

  if (!snap.exists()) return null;

  return {
    id: snap.id,
    ...snap.data(),
  };
};

export const fetchVersionBatchById = async (versionId) => {
  if (!versionId) return null;

  const snap = await getDoc(doc(versionBatchesCollectionRef(), versionId));

  if (!snap.exists()) return null;

  return {
    id: snap.id,
    ...snap.data(),
  };
};

export const buildCatalogsFromVersionBatch = (versionBatch) => {
  if (!versionBatch?.catalogs) return [];

  return PARAPLANNING_CATALOGS.map((catalogConfig) => {
    const versionCatalog = versionBatch.catalogs?.[catalogConfig.id];

    return {
      id: catalogConfig.id,
      name: versionCatalog?.catalogName || catalogConfig.name,
      code: catalogConfig.code,
      displayOrder: catalogConfig.displayOrder,
      categories: (catalogConfig.categories || []).map((categoryConfig) => {
        const versionCategory =
          versionCatalog?.snapshot?.categories?.find(
            (category) => category.id === categoryConfig.id,
          ) || null;

        return {
          id: categoryConfig.id,
          name: versionCategory?.name || categoryConfig.name,
          displayOrder:
            versionCategory?.displayOrder || categoryConfig.displayOrder || 999,
          items: (versionCategory?.items || []).map((item) => ({
            id: item.id,
            name: item.name || "",
            description: item.description || "",
            amount: Number(item.amount || 0),
            amountPrefix: item.amountPrefix || "",
            amountType: item.amountType || "fixed",
            currency: item.currency || "GBP",
            displayOrder: Number(item.displayOrder || 999),
            isActive: item.isActive !== false,
            visibleOnFrontend: item.visibleOnFrontend !== false,
            modifiers: (item.modifiers || []).map((modifier) => ({
              id: modifier.id,
              label: modifier.label || "",
              amount: Number(modifier.amount || 0),
              amountType: modifier.amountType || "fixed",
            })),
          })),
        };
      }),
    };
  });
};

export const buildNotesByCatalogFromVersionBatch = (versionBatch) => {
  if (!versionBatch?.catalogs) return {};

  return Object.values(versionBatch.catalogs).reduce((acc, catalog) => {
    acc[catalog.catalogId] = catalog.notes || "";
    return acc;
  }, {});
};